import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import MediaGatewayForm from 'routes/MediaGateway/components/MediaGatewayForm';
import { Ref } from 'routes/MediaGateway/components/MediaGatewayForm/MediaGatewayForm';
import { FormTemplateSection } from 'data/template/template.types';
import { RootState } from 'data/root.reducers';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as networkSelectors from 'data/network/network.selectors';
import * as mediaGatewayActions from 'data/mediaGateway/mediaGateway.actions';
import { AdaptedMediaGateway } from 'data/mediaGateway/mediaGateway.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newMediaGateway } from 'routes/MediaGateway/MediaGateway.types';
import { useMediaGatewayFieldCreateNew } from '../../hook/useMediaGatewayFieldCreateNew';
import { mediaGatewayTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/mediaGatewayTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/mediaGatewayTemplateDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  mediaGatewayTemplateDef: adaptTemplateVisibilityMode(
    mediaGatewayTemplate,
    'create',
  ),
  userId: state.authentication.userId,
  mediaGatewayTemplate: adaptTemplateVisibilityMode(mediaGatewayTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  networks: networkSelectors.getNetworks(state),
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
});

const mapDispatch = {
  createMediaGateway: mediaGatewayActions.createMediaGateway,
  goToMediaGatewayList: mediaGatewayActions.goToMediaGatewayList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const MediaGatewayCreate = ({
  history,
  userId,
  goToMediaGatewayList,
  createMediaGateway,
  userName,
  isAuditEnabled,
  mediaGatewayTemplateDef,
  networks,
  deliveryFunctionId
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<AdaptedMediaGateway>(
    cloneDeep(newMediaGateway),
  );
  const [template, setTemplate] = useState(mediaGatewayTemplateDef);
  const [handleFieldCreateNew] = useMediaGatewayFieldCreateNew(
    (param) => handleFieldUpdate(param),
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (template == null || formData == null) {
      return;
    }
    const adaptedTemplate = adaptTemplateOptions(template, networks);
    setTemplate(adaptedTemplate);
  }, [networks])

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AdaptedMediaGateway);
    const clonedTemplate = cloneDeep(template);
    clonedTemplate.formId = Date.now().toString();

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateMediaGatewayActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const mediaGatewayFormData = formRef.current.getValues();
    if (!mediaGatewayFormData.mediagatewaynumlist) {
      mediaGatewayFormData.mediagatewaynumlist = [];
    }
    if (mediaGatewayFormData.insidenat) {
      mediaGatewayFormData.insidenat = 'YES';
    } else {
      mediaGatewayFormData.insidenat = 'NO';
    }

    const adaptedMediaGatewayData = {
      ...mediaGatewayFormData,
      mediagatewaynumlist: mediaGatewayFormData.mediagatewaynumlist.map((mediagatewaynum) => {
        return {
          callingnum: mediagatewaynum,
          id: 0,
          cid: 0,
        }
      })
    }

    const payload = {
      data: adaptedMediaGatewayData,
      auditDetails: getAuditDetailsToSubmit(
        mediaGatewayFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateMediaGatewayActionPayload();
    if (payload) {
      createMediaGateway(payload, history);
    }
  };

  const handleCancel = () => goToMediaGatewayList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <MediaGatewayForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="MediaGatewayCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('mediaGateway.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(MediaGatewayCreate));
